/**
 * Ingredient, Packaging & Supplier Agreements — 12 documents.
 */
export const SUPPLY_DOCS = [
  {
    id: "ingredient-supply-agreement",
    name: "Ingredient Supply Agreement",
    category: "supply",
    subcategory: "Ingredients and raw materials",
    aliases: ["Ingredient Purchase Agreement", "Ingredient Supplier Agreement", "Component Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Ingredient Supplier",
    whenYouNeedIt:
      "When you buy an ingredient directly rather than letting the co-packer source it, or when a single ingredient is important enough that losing access to it would stop production.",
    whatItDoes:
      "Sets the commercial terms for repeat purchase of an ingredient: price and how it can move, minimum quantities, lead times, forecasting, the specification the material must meet, and what happens when a lot is wrong or supply runs short.",
    whyItMatters:
      "One ingredient can hold your whole product hostage. A functional botanical, a specific protein isolate, a flavour system built for you — if the supplier allocates you out during a shortage, changes origin without notice, or raises price knowing you have no second source, the consequence is not a procurement problem, it is a product you cannot make. The terms that decide all three are the ones founders skim because the first order was small.",
    majorTerms: [
      "Material specification and version referenced by the agreement",
      "Price, price validity period and escalation basis",
      "Minimum order quantity and any annual volume commitment",
      "Lead times and delivery terms",
      "Forecasting obligations and which portion becomes binding",
      "Allocation rules during shortage",
      "Certificate of Analysis requirements per lot",
      "Certifications: organic, kosher, non-GMO, gluten-free, allergen status",
      "Regulatory documentation the supplier must provide and keep current",
      "Substitution, origin change and sub-supplier notification",
      "Non-conforming material, rejection window and remedy",
      "Recall cooperation and traceability response time",
      "Term, renewal and termination notice",
    ],
    clausesToWatch: [
      {
        clause: "Price escalation basis",
        why: "An escalation clause tied to the supplier's own cost assessment rather than a documented index lets price move on assertion alone.",
      },
      {
        clause: "Allocation during shortage",
        why: "Without a stated allocation rule, a tight market means the biggest customers get supplied first and a small brand simply stops producing.",
      },
      {
        clause: "Substitution and origin change rights",
        why: "A change of origin or sub-supplier can alter allergen status, certification eligibility and how the ingredient performs, while the paperwork looks unchanged.",
      },
      {
        clause: "Rejection window and remedy",
        why: "A short inspection window and a replacement-only remedy mean a bad lot discovered during production costs you a full run and recovers only the ingredient.",
      },
      {
        clause: "Minimum purchase commitments",
        why: "A volume commitment set at launch projections becomes a bill for ingredient you never used when the forecast does not land.",
      },
      {
        clause: "Termination notice and last-time-buy rights",
        why: "A supplier who can exit on short notice with no final purchase right can strand you with no material and no time to qualify an alternative.",
      },
    ],
    cpgIssues: [
      "Agricultural and botanical ingredients vary by crop year and growing region, so the specification needs ranges that accommodate real variation without letting the finished product drift.",
      "Certificates for organic, kosher, non-GMO and similar claims expire; the agreement should require current documents on file and prompt notice of any lapse, because the claim on your pack depends on them.",
      "Ingredient shelf life is often shorter than the time it takes a young brand to consume a supplier minimum, so a minimum order can expire in the warehouse before it is used.",
      "A COA needs named parameters, methods and actual values — a certificate that says 'conforms' is unusable when you are trying to work out why a run went wrong.",
      "Allergen status depends on the supplier's facility as well as the ingredient, and a supplier site change can make your label statement wrong.",
      "Regulatory paperwork travels with the ingredient — the supplier's facility registration, food-use assurances and the documentation behind any claim are what a retailer or an insurer eventually asks you for, so name what the supplier must supply and keep current.",
      "Where the co-packer holds the buying relationship, you may have no direct rights against the supplier at all, which matters when the ingredient causes a problem.",
    ],
    founderMistakes: [
      "Buying an ingredient on a purchase order for a year, then losing supply in a shortage because there was no agreement and no allocation right.",
      "Committing to an annual volume to get a better price, and paying for material against a forecast that never materialised.",
      "Ordering to a supplier minimum without checking the ingredient's shelf life against the production schedule, and writing off half of it.",
      "Accepting COAs with no values, so a failed production run could not be traced back to the input.",
      "Not requiring notice of origin changes, and finding a certification claim on the pack was no longer supported.",
    ],
    negotiationPoints: [
      "Tie any price escalation to a documented, verifiable basis with a notice period and a right to terminate if you will not accept it.",
      "Ask for a stated allocation rule that protects a defined baseline volume during shortage.",
      "Require notice and approval before any change of origin, site or sub-supplier.",
      "Extend the rejection window to when the material is actually used, not when it is delivered.",
      "Ask for a remedy that reaches the cost of the affected production run, not just replacement of the ingredient.",
      "Secure a last-time-buy right and a transition period if the supplier discontinues the material.",
    ],
    redFlags: [
      "Price changeable at the supplier's discretion with no notice.",
      "No allocation commitment of any kind during shortage.",
      "Silence on substitution, origin or sub-supplier changes.",
      "A rejection window measured in a few days from delivery.",
      "Volume commitments with no relief mechanism if your demand shifts.",
      "No traceability or recall cooperation obligation.",
    ],
    beforeYouSign: [
      "A written material specification with ranges you have checked against real lots.",
      "A sample COA showing what the supplier actually reports.",
      "Current certification documents with expiry dates for every claim your label depends on.",
      "A realistic consumption model checked against the ingredient's shelf life and the supplier's minimum.",
    ],
    related: [
      "supplier-quality-agreement",
      "master-supply-agreement",
      "raw-material-supply-agreement",
      "co-packer-agreement",
      "recall-responsibility-agreement",
      "supplier-nda",
    ],
    attorneyReview: "yes",
    templateId: "ingredient-supply-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "packaging-supply-agreement",
    name: "Packaging Supply Agreement",
    category: "supply",
    subcategory: "Packaging",
    aliases: ["Packaging Purchase Agreement", "Component Supply Agreement", "Packaging Vendor Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Packaging Supplier",
    whenYouNeedIt:
      "Before ordering any custom or printed packaging component — cans, bottles, labels, cartons, closures, film — where the order is large enough or slow enough that a change of plan is expensive.",
    whatItDoes:
      "Covers the repeat purchase of packaging components: minimums, lead times, pricing, artwork approval, tooling and plate ownership, what happens to unused stock, and who carries the cost when a design or a formula changes.",
    whyItMatters:
      "Packaging is where a young brand's cash gets trapped. Print minimums are set by the press, not by your sales, so the smallest order is often several times your next production run. Add long lead times and the result is a warehouse full of components bought against a forecast. Then a formula tweak or a label revision arrives and the whole minimum order becomes waste. Who eats obsolete packaging is one of the most expensive terms in CPG and one of the least often written down.",
    majorTerms: [
      "Component list, part numbers and specifications",
      "Minimum order quantity per component and per print run",
      "Price tiers by quantity and price validity period",
      "Lead times from artwork approval to delivery",
      "Tooling, plate, cylinder and die ownership and storage",
      "Artwork approval process, proofing and version control",
      "Obsolete and excess inventory responsibility on an artwork or specification change",
      "Storage of finished components and storage charges",
      "Delivery destination, including shipment direct to the co-packer",
      "Inbound acceptance criteria and defect thresholds",
      "Overrun and underrun tolerance on print runs",
      "Term, termination and disposition of remaining stock",
    ],
    clausesToWatch: [
      {
        clause: "Obsolete inventory responsibility",
        why: "When a formula change or a label revision makes printed stock unusable, the party who pays for it is decided by this clause and by nothing else.",
      },
      {
        clause: "Tooling, plate and die ownership",
        why: "If the supplier owns the tooling you paid for, moving to another supplier means paying to make it again and waiting out the lead time.",
      },
      {
        clause: "Overrun and underrun tolerance",
        why: "A wide tolerance means you are invoiced for materially more than you ordered, or receive materially less and cannot complete a production run.",
      },
      {
        clause: "Artwork approval and liability for errors",
        why: "Approval sign-off usually transfers responsibility for an error to you, so the proofing step is the last point at which a mistake is cheap.",
      },
      {
        clause: "Price validity and escalation",
        why: "Quotes given at one commodity level can be repriced between order and delivery unless the validity period is stated.",
      },
      {
        clause: "Storage terms for finished components",
        why: "Components produced to a minimum have to live somewhere, and storage charged from day one can wipe out the saving that justified the minimum.",
      },
    ],
    cpgIssues: [
      "Print and forming minimums are set by the equipment, so a first order is often several production runs' worth of packaging bought before you know how the product sells.",
      "Lead times on printed cans and custom bottles are measured in months, which means packaging decisions are locked in long before you know whether the formula or label will change.",
      "A label revision forced by a formula change, an ingredient substitution or a claim update can strand an entire minimum order — this is the single most common way a young brand writes off five figures of packaging.",
      "Components must be compatible with the specific filling line: neck finish, closure torque, label wind, case erector settings all vary between plants.",
      "Shipping components directly to the co-packer saves handling but transfers a receiving and inspection step to someone with no incentive to reject a marginal lot, so acceptance criteria need to be written down.",
      "Artwork version control links this agreement to the product specification; a supersession that reaches the printer late produces packaging for a product that no longer exists.",
    ],
    founderMistakes: [
      "Ordering a full print minimum before finalising the ingredient statement, then reformulating and writing off the entire order.",
      "Paying for plates and dies without securing ownership, and being quoted for new ones when moving supplier.",
      "Not checking the overrun tolerance, and being invoiced for substantially more units than ordered.",
      "Assuming storage was included, then receiving backdated storage charges on components held for months.",
      "Approving artwork from a screen proof rather than a physical proof, and receiving a colour that did not match the rest of the range.",
    ],
    negotiationPoints: [
      "Get explicit written confirmation that tooling, plates, dies and cylinders you funded belong to you and will be released on request.",
      "Ask for a defined obsolescence process: notice, a use-up window and a stated cap on your liability.",
      "Narrow the overrun and underrun tolerance and require billing on quantity actually delivered.",
      "Require a physical proof or first article before every full print run.",
      "Negotiate a free storage window sized to your production cycle, with stated rates after it.",
      "Ask for price validity through the lead time, so a quote survives to delivery.",
    ],
    redFlags: [
      "No statement of who owns tooling or plates you paid for.",
      "Silence on obsolete stock when artwork or specification changes.",
      "Wide overrun tolerance billed at full price.",
      "Storage charges beginning on completion with no free period.",
      "No inbound acceptance criteria or defect remedy.",
      "Price quoted without a validity period on a component with a long lead time.",
    ],
    beforeYouSign: [
      "Final artwork with a version number and a physical proof approved against it.",
      "Written confirmation from the co-packer that the components run on their line.",
      "A realistic consumption forecast checked against the print minimum and the lead time.",
      "The storage schedule and the tooling ownership position in writing.",
    ],
    related: [
      "packaging-specification-agreement",
      "can-supply-agreement",
      "label-supply-agreement",
      "carton-supply-agreement",
      "packaging-artwork-ownership",
      "master-supply-agreement",
    ],
    attorneyReview: "yes",
    templateId: "packaging-supply-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "can-supply-agreement",
    name: "Can Supply Agreement",
    category: "supply",
    subcategory: "Packaging",
    aliases: ["Aluminum Can Agreement", "Can and End Supply Agreement", "Beverage Can Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Can Manufacturer or Can Distributor",
    whenYouNeedIt:
      "Before your first canned production run, and again whenever you move from sleeved brite cans to printed cans, change can size, or change the plant that fills them.",
    whatItDoes:
      "Covers the purchase of cans and ends: minimums, lead times, decoration, price and how aluminium cost moves through it, delivery in truckload quantities, and what happens to printed stock when the artwork changes.",
    whyItMatters:
      "Cans are the hardest packaging commitment a beverage founder makes. Printed can minimums are large enough that a first order can represent more inventory than the brand will sell in a year, lead times run in months, and the cans arrive by the truckload into warehouse space you may not have. Every one of those pressures is why brands start on shrink-sleeved brite cans, and why the decision to move to printed cans deserves the same scrutiny as a fundraise.",
    majorTerms: [
      "Can body specification: size, gauge, neck finish, coating",
      "End and tab specification and whether ends are supplied separately",
      "Decoration method: printed, brite for sleeving, or labelled",
      "Minimum order quantity, expressed in layers, pallets or truckloads",
      "Lead time from artwork approval to delivery",
      "Price and the mechanism for aluminium cost movement",
      "Plate and print cylinder charges and ownership",
      "Delivery terms, truckload quantities and destination",
      "Storage, free storage period and pallet-position charges",
      "Inbound acceptance: dents, ovality, scuffing, contamination",
      "Allocation during periods of tight supply",
      "Obsolete printed stock on an artwork or specification change",
    ],
    clausesToWatch: [
      {
        clause: "Aluminium cost pass-through mechanism",
        why: "A pass-through tied to an undefined internal cost measure lets price move without any way to check it against the market.",
      },
      {
        clause: "Minimum order quantity and the unit it is expressed in",
        why: "Minimums quoted in truckloads or layers rather than cans hide how much inventory you are actually committing to.",
      },
      {
        clause: "Allocation during tight supply",
        why: "Can capacity tightens periodically, and without an allocation commitment a small brand is the first customer to be deprioritised.",
      },
      {
        clause: "Obsolete printed can liability",
        why: "Printed cans cannot be repurposed, so an artwork or formula change turns the remaining stock into scrap aluminium at your cost.",
      },
      {
        clause: "Delivery and storage terms",
        why: "A truckload of cans consumes a large number of pallet positions, and storage charges or a delivery you cannot receive both create immediate cost.",
      },
      {
        clause: "Inbound defect threshold",
        why: "Dented and out-of-round cans jam a filler and are rejected at speed, and without a stated threshold and remedy the loss is yours.",
      },
    ],
    cpgIssues: [
      "Printed can minimums are typically far larger than sleeved brite can minimums, which is why most brands launch on sleeves and switch only once volume is proven — the switch is a cash decision, not a design one.",
      "Can neck finish and end specification must match the seamer at the specific plant that will fill them; a can that runs at one co-packer may not run at another.",
      "Cans are bulky relative to their value, so a full order can occupy more warehouse space than the finished product it becomes, and shipping direct to the co-packer needs their agreement and their space.",
      "Ends and bodies are sometimes ordered and delivered separately, and a shortage of one stops production as effectively as a shortage of both.",
      "Coating and liner selection interacts with the product: acidic, high-salt and certain functional formulations behave differently against different internal coatings, and this belongs in the specification.",
      "Damage in transit is common with palletised cans, so acceptance criteria and a claims process matter more than they do for most components.",
    ],
    founderMistakes: [
      "Committing to a printed can minimum before the formula was final, then reformulating and scrapping the remaining stock.",
      "Not confirming warehouse space for a truckload delivery, and paying demurrage and emergency storage on arrival.",
      "Ordering cans without confirming the neck and end specification against the co-packer's seamer, and losing a booked production slot.",
      "Accepting a pass-through clause with no defined basis, so aluminium price increases arrived without documentation.",
      "Ordering bodies and ends on different lead times, and having a full pallet of cans and no ends on production day.",
    ],
    negotiationPoints: [
      "Ask for the aluminium component of price to move against a documented, published reference rather than an internal assessment.",
      "Get the minimum expressed in units as well as in truckloads or layers, so the commitment is visible.",
      "Ask for a baseline allocation commitment during periods of constrained supply.",
      "Confirm plate and cylinder ownership in writing where you funded them.",
      "Negotiate a free storage window and direct delivery to the co-packer where they can receive it.",
      "Set inbound defect thresholds with a credit or replacement remedy above them.",
    ],
    redFlags: [
      "Pass-through pricing with no defined reference.",
      "No allocation commitment during shortage.",
      "Minimums quoted only in truckloads with no unit equivalent.",
      "No stated remedy for damaged or out-of-specification cans on arrival.",
      "Storage charges from delivery with no free period.",
      "Silence on obsolete printed stock.",
    ],
    beforeYouSign: [
      "Written confirmation from the co-packer that the can and end specification runs on their seamer and filler.",
      "A finalised formula and label, since printed cans cannot absorb a later change.",
      "Confirmed warehouse capacity, or a written agreement from the co-packer to receive and store the delivery.",
      "The full landed cost per can including freight, storage and expected damage rate.",
    ],
    related: [
      "packaging-supply-agreement",
      "packaging-specification-agreement",
      "label-supply-agreement",
      "co-packer-agreement",
      "warehouse-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "bottle-supply-agreement",
    name: "Bottle Supply Agreement",
    category: "supply",
    subcategory: "Packaging",
    aliases: ["PET Bottle Agreement", "Glass Bottle Supply Agreement", "Container Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Bottle Manufacturer",
    whenYouNeedIt:
      "Before ordering bottles, and particularly before commissioning a custom bottle, where a mould has to be cut before a single unit exists.",
    whatItDoes:
      "Covers the purchase of bottles or jars: specification and neck finish, stock versus custom design, mould and tooling ownership, resin or glass cost movement, minimums, lead times and delivery.",
    whyItMatters:
      "The choice between a stock bottle and a custom one is the choice between a component you can reorder from several suppliers and a component that exists only in one company's mould. A custom bottle is a real brand asset and a real dependency: if the mould belongs to the supplier, or sits in their plant with no right of removal, your packaging supply has exactly one source and they know it. Add closure compatibility and line performance and a bottle decision reaches further into the business than it looks.",
    majorTerms: [
      "Bottle specification: capacity, weight, dimensions, material, colour",
      "Neck finish and closure compatibility",
      "Stock design or custom, and the design ownership position",
      "Mould and tooling cost, ownership, location and removal rights",
      "Cavity count and its effect on lead time and minimum",
      "Minimum order quantity and price tiers",
      "Resin or glass cost movement mechanism",
      "Lead times, including tooling lead time for a custom bottle",
      "Line compatibility: filler, capper, labeller and case packer",
      "Inbound acceptance: dimensional tolerance, breakage, cosmetic defects",
      "Storage and delivery terms",
      "Term, termination and disposition of tooling on exit",
    ],
    clausesToWatch: [
      {
        clause: "Mould and tooling ownership",
        why: "A mould you paid for but do not own turns a supplier change into a re-tooling project with its own lead time and cost.",
      },
      {
        clause: "Right to remove or relocate tooling",
        why: "Ownership without a removal right is ownership on paper only, and the tooling stays in their plant while you look for an alternative.",
      },
      {
        clause: "Neck finish and closure compatibility",
        why: "A mismatch between bottle and closure is discovered at capping, on production day, with the line running and product in the tank.",
      },
      {
        clause: "Design ownership for a custom bottle",
        why: "If the supplier retains rights in a bottle design you commissioned, they can offer the same shape to another brand.",
      },
      {
        clause: "Light-weighting and specification changes",
        why: "Suppliers reduce material to manage cost, and a lighter bottle can behave differently on the line, in the case and under a hot fill.",
      },
      {
        clause: "Breakage and damage allowance",
        why: "Glass in particular arrives with a loss rate, and an undefined allowance means every broken pallet is absorbed by you.",
      },
    ],
    cpgIssues: [
      "Hot fill, cold fill, carbonation and retort each demand different bottle construction, and a bottle chosen for appearance can fail the process it has to survive.",
      "Closure torque and liner selection determine whether a bottle seals and holds shelf life; these are specification items, not assembly details.",
      "Panel and base design on PET affects how a bottle behaves under vacuum after a hot fill, and a change made for material saving can cause visible deformation on shelf.",
      "Glass adds weight to every freight load and a breakage rate to every leg of the journey, both of which belong in the landed cost model rather than being discovered later.",
      "A custom bottle sets the case dimensions and the pallet pattern, so a design decision propagates into freight cost and retailer receiving.",
      "Label application depends on bottle geometry and surface, so the bottle and label specifications need to be agreed together rather than sequentially.",
    ],
    founderMistakes: [
      "Paying for a custom mould without securing ownership or a removal right, and being unable to move supplier without re-tooling.",
      "Choosing a bottle on shelf appeal without confirming it survived the fill process, and losing a full run to deformation.",
      "Not checking closure compatibility with the actual capper at the co-packer, and stopping a booked line.",
      "Ignoring breakage on glass in the cost model, then finding the effective cost per saleable unit materially higher than quoted.",
      "Accepting a light-weighted replacement without a qualification run, and having bottles collapse in the case during transit.",
    ],
    negotiationPoints: [
      "Secure written ownership of tooling you funded, plus an express right to remove or relocate it on notice.",
      "Ask for exclusivity on a custom bottle design you commissioned and paid to develop.",
      "Require notice and a qualification run before any light-weighting or specification change.",
      "Confirm neck finish and closure compatibility in writing, tested against the actual capping equipment.",
      "Agree a breakage and defect allowance with a credit mechanism above it.",
      "Tie resin or glass cost movement to a documented reference with a notice period.",
    ],
    redFlags: [
      "Tooling paid for by you but owned by the supplier.",
      "Ownership with no right to remove the mould.",
      "No commitment on design exclusivity for a custom bottle.",
      "Specification changes permitted without notice or qualification.",
      "No breakage or dimensional defect allowance.",
    ],
    beforeYouSign: [
      "Written confirmation of tooling ownership and removal rights.",
      "A trial or qualification result showing the bottle survives your fill process.",
      "Confirmed compatibility with the closure and with the co-packer's capping and labelling equipment.",
      "The case and pallet configuration the bottle produces, checked against your freight and retailer requirements.",
    ],
    related: [
      "packaging-supply-agreement",
      "packaging-specification-agreement",
      "label-supply-agreement",
      "co-packer-agreement",
      "design-ownership-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "label-supply-agreement",
    name: "Label Supply Agreement",
    category: "supply",
    subcategory: "Packaging",
    aliases: ["Printing Agreement", "Shrink Sleeve Supply Agreement", "Pressure Sensitive Label Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Label Printer",
    whenYouNeedIt:
      "Before the first label order, and again every time the artwork changes — which happens more often than founders expect, because ingredient statements, claims and net contents all live on the label.",
    whatItDoes:
      "Covers the printing and supply of labels or sleeves: substrate and adhesive, print method and colour tolerance, plate or cylinder charges, minimums, lead times, roll format for the applicator, and version control over the artwork being printed.",
    whyItMatters:
      "Labels are cheap per unit and expensive per mistake. Print minimums are set by the press, so the smallest order is usually several production runs' worth. That would be fine if labels were stable, but they are the most change-prone component you own: a supplier substitution changes the ingredient statement, a claim gets refined, a net content or serving figure moves, and every one of those makes the remaining stock unusable. A label revision forced by a formula or regulatory change can strand a full minimum order, and the clause deciding who pays for that is often not in the agreement at all.",
    majorTerms: [
      "Label format: pressure sensitive, shrink sleeve or cut and stack",
      "Substrate, adhesive and liner specification",
      "Print method, colour set and colour tolerance",
      "Plate, cylinder and die charges and ownership",
      "Minimum order quantity per SKU and per print run",
      "Roll specification: wind direction, core size, labels per roll",
      "Lead time from artwork approval to delivery",
      "Proofing and approval process, including press checks",
      "Artwork version control and responsibility for printing a superseded file",
      "Obsolete stock responsibility when artwork changes",
      "Overrun and underrun tolerance",
      "Inbound acceptance: registration, colour, adhesion, roll defects",
    ],
    clausesToWatch: [
      {
        clause: "Obsolete label stock on an artwork change",
        why: "A formula or claim change makes existing labels unusable overnight, and without a written position the entire remaining minimum order is your write-off.",
      },
      {
        clause: "Plate and cylinder ownership",
        why: "Plates you funded but do not own become a repeat charge every time you move printer or reissue a design.",
      },
      {
        clause: "Colour tolerance and proofing",
        why: "Without an agreed tolerance and an approved physical proof, a run that looks wrong beside the rest of your range is still contractually acceptable.",
      },
      {
        clause: "Roll format and wind direction",
        why: "Labels wound the wrong way cannot be run on the applicator, which makes a technically conforming order useless on production day.",
      },
      {
        clause: "Adhesive suitability for the container and its conditions",
        why: "An adhesive that fails on a chilled, wet or frozen container produces labels lifting in a distributor's warehouse, which is a returns problem, not a printing one.",
      },
      {
        clause: "Responsibility for printing against a superseded artwork version",
        why: "If version control is informal, the party who approved last is left arguing about which file was current after a full run has already printed.",
      },
    ],
    cpgIssues: [
      "The ingredient statement and allergen declaration live on the label, so any ingredient substitution upstream can invalidate every label in your warehouse.",
      "Net contents, serving information and claim language change more often than founders expect, and each change is a new plate charge plus a new minimum.",
      "Shrink sleeves distort artwork as they conform to the container, so the design has to be distortion-compensated for the specific bottle or can, and a container change breaks it.",
      "Adhesive and facestock must match real conditions: condensation on chilled beverages, ice bath immersion, freezer storage and hot fill all defeat a general-purpose adhesive.",
      "Roll direction, core size and unwind orientation must match the applicator at the specific co-packer, and a plant change can make existing rolls unusable.",
      "Date code and lot code are often applied over the label at the plant, so the artwork must leave a readable clear area in a place the coder can reach.",
    ],
    founderMistakes: [
      "Ordering a full label minimum before the ingredient statement was final, then substituting an ingredient and scrapping the lot.",
      "Not specifying wind direction, and receiving a minimum order the co-packer's applicator could not run.",
      "Approving artwork from a digital proof only, and receiving a colour materially different from the rest of the range.",
      "Using a standard adhesive on a chilled beverage, and having labels lift in a distributor's cold warehouse.",
      "Designing artwork with no clear area for the date code, so lot codes were printed over the design and could not be read.",
    ],
    negotiationPoints: [
      "Agree in writing who bears obsolete label cost when a change originates on your side versus a supplier or specification change.",
      "Ask for staged or scheduled releases against one plate set, so you can order in smaller increments after the first run.",
      "Confirm plate and cylinder ownership where you paid for them.",
      "Require a physical proof or press check before every new design and a colour tolerance you can measure.",
      "Specify roll format precisely, confirmed against the co-packer's applicator.",
      "Set inbound acceptance criteria covering registration, colour, adhesion and roll defects, with a replacement remedy.",
    ],
    redFlags: [
      "No position on obsolete stock when artwork changes.",
      "Plate charges billed to you with ownership retained by the printer.",
      "No colour tolerance and no physical proofing step.",
      "Roll specification left to the printer's default.",
      "No adhesive qualification against the actual container and storage conditions.",
    ],
    beforeYouSign: [
      "Final artwork with a version number, matched to the ingredient statement on the product specification.",
      "The co-packer's applicator requirements: wind, core, roll diameter and labels per roll.",
      "An adhesive recommendation tested against the container and its real storage conditions.",
      "A realistic release schedule so the first order is not larger than your near-term need.",
    ],
    related: [
      "packaging-supply-agreement",
      "packaging-specification-agreement",
      "packaging-artwork-ownership",
      "product-specification-sheet",
      "can-supply-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "carton-supply-agreement",
    name: "Carton Supply Agreement",
    category: "supply",
    subcategory: "Packaging",
    aliases: ["Corrugated Supply Agreement", "Case and Tray Agreement", "Shipper Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Corrugated or Carton Manufacturer",
    whenYouNeedIt:
      "Before the first production run that needs cases, trays or shelf-ready packaging, and again whenever case count, product dimensions or a retailer's presentation requirement changes.",
    whatItDoes:
      "Covers the supply of shipping cases, trays and shelf-ready cartons: board grade and construction, dimensions, print, case count, pallet configuration, minimums and lead times.",
    whyItMatters:
      "Cases look like the least interesting component and quietly decide freight cost, warehouse efficiency and whether a retailer's receiving dock accepts your pallet. A case that is a few millimetres out breaks the pallet pattern, which costs money on every load for as long as the design exists. A case that fails a drop or compression expectation produces crushed product at the back of a trailer. And because corrugated ships air, a poor case decision is paid for in freight on every delivery, not once.",
    majorTerms: [
      "Board grade, flute and construction",
      "Internal and external dimensions with tolerances",
      "Case count and product arrangement inside the case",
      "Pallet pattern, tie and high, and stretch wrap requirements",
      "Print method, colour count and plate charges",
      "Barcode placement, symbology and print quality requirement",
      "Compression and stacking expectations for the intended supply chain",
      "Minimum order quantity and price tiers",
      "Lead time and delivery terms",
      "Storage and moisture conditions",
      "Shelf-ready or retail-ready construction requirements",
      "Inbound acceptance: dimensional, print and board defects",
    ],
    clausesToWatch: [
      {
        clause: "Dimensional tolerance",
        why: "A case slightly out of tolerance breaks the pallet pattern or jams the case erector, and the cost repeats on every run and every load.",
      },
      {
        clause: "Board grade substitution rights",
        why: "A downgrade made for cost can produce cases that fail in a stacked trailer or a warehouse rack, and the damage appears at the retailer.",
      },
      {
        clause: "Barcode print quality",
        why: "A case barcode that fails verification at a retailer's dock produces a rejected delivery and a chargeback, not a reprint.",
      },
      {
        clause: "Obsolete stock on a case count or dimension change",
        why: "A change in case count makes printed cases useless, and printed corrugated has no secondary use.",
      },
      {
        clause: "Storage conditions and moisture",
        why: "Corrugated stored in a damp warehouse loses strength before it is used, and the failure appears as crushed product later.",
      },
      {
        clause: "Shelf-ready specification changes driven by a retailer",
        why: "Retailers change presentation requirements on their own schedule, and a case built to an old requirement can be refused at the dock.",
      },
    ],
    cpgIssues: [
      "Case dimensions determine the pallet pattern, which determines how many cases ship per truck — a poorly configured case is a permanent freight surcharge.",
      "Beverage cases are heavy for their size, so compression performance matters in a way it does not for lighter categories, and cases stacked in a warehouse rack carry more load than the design assumed.",
      "Shelf-ready and retail-ready formats are specified by individual retailers and change between them, so one case design rarely serves every account.",
      "Case barcodes are scanned at receiving, and a marginal print quality that passes in your office fails at a dock and produces a deduction.",
      "Corrugated absorbs moisture in cold and humid storage, which matters for chilled and frozen products and belongs in the storage terms.",
      "A change in fill volume or bottle size changes the case, so packaging changes cascade further than the component being changed.",
    ],
    founderMistakes: [
      "Designing a case around the product without checking the pallet pattern, and paying extra freight on every load for a year.",
      "Accepting a board grade downgrade to save cost, and receiving crushed product from the bottom of pallets.",
      "Printing cases before the case count was final, and scrapping the order when the retailer required a different pack size.",
      "Never verifying case barcode print quality, and taking chargebacks on refused deliveries.",
      "Storing corrugated in an unheated warehouse over a wet season and finding the cases had lost strength before use.",
    ],
    negotiationPoints: [
      "Set dimensional tolerances tight enough to protect the pallet pattern and the case erector settings.",
      "Require notice and approval before any board grade or construction change.",
      "Require barcode verification to a stated grade rather than visual checking.",
      "Ask for staged releases against a single plate set so the first order matches near-term need.",
      "Agree storage conditions and a shelf life for the corrugated itself.",
      "Confirm plate ownership where you funded the plates.",
    ],
    redFlags: [
      "No dimensional tolerance stated.",
      "Board grade substitution permitted without notice.",
      "No barcode print quality requirement.",
      "Full minimum required with no staged release option.",
      "No inbound acceptance criteria for print or dimensional defects.",
    ],
    beforeYouSign: [
      "The pallet pattern the case produces, checked against your freight carrier and your retailers.",
      "Confirmation the case runs on the co-packer's case erector and packer.",
      "Any shelf-ready specification required by the retailers you are targeting.",
      "A barcode verification result on a physical sample case.",
    ],
    related: [
      "packaging-supply-agreement",
      "packaging-specification-agreement",
      "can-supply-agreement",
      "three-pl-agreement",
      "retail-vendor-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "raw-material-supply-agreement",
    name: "Raw Material Supply Agreement",
    category: "supply",
    subcategory: "Ingredients and raw materials",
    aliases: ["Commodity Supply Agreement", "Bulk Material Agreement", "Input Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand or Co-Packer + Raw Material Supplier",
    whenYouNeedIt:
      "When you buy bulk inputs — sweeteners, dairy, juice concentrate, protein, botanicals, oils — in quantities where price movement and availability materially affect your cost of goods.",
    whatItDoes:
      "Covers repeat purchase of a bulk commodity input: contracted versus spot pricing, quantity commitments, delivery and storage, quality on receipt, and how crop and market variation is handled between the parties.",
    whyItMatters:
      "Bulk inputs behave differently from manufactured ingredients. Price moves with markets and harvests rather than with a supplier's cost base, availability moves with weather, and quality varies naturally from lot to lot. A contract that ignores all three either locks you into a price the market has left behind, or leaves you fully exposed to a move you cannot pass on to a retailer who fixed your price a quarter ago. The gap between input volatility and fixed retail pricing is where CPG margin is won and lost.",
    majorTerms: [
      "Material specification with ranges reflecting natural variation",
      "Contract quantity and whether it is a firm commitment or a maximum",
      "Pricing: fixed for a period, indexed, or spot",
      "Price escalation basis and notice",
      "Crop year, harvest and origin specification",
      "Delivery schedule, form and packaging of the bulk material",
      "Storage requirements and shelf life after receipt",
      "Sampling and testing on receipt, and rejection rights",
      "Allocation and force majeure during shortage",
      "Substitution and origin change notification",
      "Food safety documentation and certifications",
      "Term, renewal and termination",
    ],
    clausesToWatch: [
      {
        clause: "Firm quantity commitment",
        why: "A take-or-pay style commitment made on optimistic projections becomes an invoice for material you cannot use and cannot store.",
      },
      {
        clause: "Force majeure scope",
        why: "A broad clause covering ordinary market disruption lets a supplier walk away from a fixed price when the market moves against them, while you stay bound when it moves your way.",
      },
      {
        clause: "Specification ranges for natural variation",
        why: "Ranges too tight cause constant rejections on material that is normal; ranges too wide let the finished product drift between production runs.",
      },
      {
        clause: "Sampling and rejection on receipt",
        why: "Bulk material is often committed to a batch before anyone tests it, so the rejection right needs to survive past delivery.",
      },
      {
        clause: "Origin and crop year specification",
        why: "Origin affects certification eligibility, flavour profile and food safety controls, and a silent change alters the product.",
      },
      {
        clause: "Allocation during shortage",
        why: "Harvest failures and supply shocks are normal in agricultural inputs, and a small buyer without an allocation right simply stops receiving material.",
      },
    ],
    cpgIssues: [
      "Crop year and growing region change flavour, colour and functional performance of agricultural inputs, so a specification that only names the material is not enough.",
      "Bulk material arrives in totes, drums or supersacks that need appropriate storage — a purchase that saves on unit cost can cost more in warehouse space and handling.",
      "Shelf life after receipt is often short relative to the quantity that makes the price attractive, so the commitment has to be modelled against real consumption.",
      "Certification chains for organic and similar claims run through every handler, so a change anywhere in the chain can affect what you may say on a pack.",
      "Sampling on receipt is the only chance to catch a problem before it enters a batch, and the testing plan needs to be assigned to a party by name.",
      "Where the co-packer buys the bulk input, the specification you rely on lives in their agreement, not yours, and you need visibility into it.",
    ],
    founderMistakes: [
      "Signing a firm annual quantity to secure a price, then paying for material the business never consumed.",
      "Buying a large quantity at an attractive price without checking storage requirements, and paying more in warehousing than was saved.",
      "Writing a specification with no allowance for crop variation, and rejecting lots that were entirely normal.",
      "Having no allocation right in a shortage, and losing supply while larger customers were served.",
      "Relying on the co-packer's ingredient purchase without ever seeing the specification behind it.",
    ],
    negotiationPoints: [
      "Structure the commitment as a maximum or a range rather than a firm take-or-pay quantity where you can.",
      "Make force majeure mutual and exclude ordinary market or price movement from it.",
      "Set specification ranges informed by actual lots across more than one crop year.",
      "Keep a rejection right that runs from when the material is tested or used, not only from delivery.",
      "Ask for a baseline allocation during shortage, tied to your historical volume.",
      "Require notice of any change in origin, crop year or handler affecting your certifications.",
    ],
    redFlags: [
      "A firm take-or-pay quantity with no relief mechanism.",
      "One-sided force majeure available only to the supplier.",
      "No allocation commitment in a shortage.",
      "No origin or crop year specification.",
      "Rejection rights expiring on delivery.",
    ],
    beforeYouSign: [
      "A consumption model based on realistic production volume rather than sales projections.",
      "Storage capacity and conditions confirmed for the form the material arrives in.",
      "Specification ranges checked against real lots from more than one season.",
      "Certification documentation covering every handler in the chain your claims depend on.",
    ],
    related: [
      "ingredient-supply-agreement",
      "supplier-quality-agreement",
      "master-supply-agreement",
      "co-packer-agreement",
      "purchase-order-terms",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "purchase-agreement",
    name: "Purchase Agreement",
    category: "supply",
    subcategory: "Purchasing",
    aliases: ["Sale of Goods Agreement", "Asset Purchase (Goods)", "One-Time Purchase Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Buyer + Seller",
    whenYouNeedIt:
      "For a one-off purchase big enough to deserve paper: a piece of production equipment, a block of ingredient from a brand that is winding down, a batch of finished goods, or leftover packaging from another company.",
    whatItDoes:
      "Documents a single sale of goods: what is being sold, in what condition, for how much, when title and risk pass, what inspection rights the buyer has, and what warranties, if any, come with it.",
    whyItMatters:
      "One-off purchases are where founders buy problems cheaply. Discounted ingredient with an unclear history, equipment sold as seen, packaging from a discontinued brand — each of these can be a bargain or a write-off, and the difference is what the paper says about condition, provenance and inspection. In food and beverage the provenance question is not academic: material with no documented storage history and no certificates cannot responsibly go into product you sell.",
    majorTerms: [
      "Description of the goods, quantity and condition",
      "Price, payment timing and any deposit",
      "Delivery, collection and who arranges transport",
      "When title and risk of loss pass",
      "Inspection rights and the period to reject",
      "Warranties given, and any express disclaimer of them",
      "Documentation transferring with the goods",
      "Seller's representations about provenance and storage history",
      "Remedies for goods that are not as described",
    ],
    clausesToWatch: [
      {
        clause: "As-is sale with disclaimed warranties",
        why: "An as-is purchase moves every risk about condition to you, which matters most for goods whose defects are not visible.",
      },
      {
        clause: "Inspection period",
        why: "A short or absent inspection window on goods you cannot practically assess at collection leaves you with no route to reject.",
      },
      {
        clause: "Title and risk of loss timing",
        why: "If risk passes on collection but you did not arrange the transport, damage in transit is yours with no claim against the carrier you did not book.",
      },
      {
        clause: "Provenance and documentation",
        why: "Ingredients or packaging arriving without certificates, lot records or storage history may be unusable in a product you intend to sell.",
      },
      {
        clause: "Remedies for non-conforming goods",
        why: "Without a stated remedy, a discounted purchase that turns out to be wrong is simply a loss.",
      },
    ],
    cpgIssues: [
      "Ingredient bought second-hand without lot records, certificates and storage history cannot support the claims or the traceability your finished product needs.",
      "Discounted packaging from a discontinued brand often carries the wrong dimensions, the wrong barcode or a design element you cannot legally or practically use.",
      "Food contact equipment sold as seen may need cleaning validation and may not suit the process it is being bought for.",
      "Short-dated ingredient sold at a discount can expire before your production schedule reaches it, which turns the saving into disposal cost.",
      "Chilled or frozen goods bought from a third party have a cold chain history you cannot verify unless the agreement requires the records.",
    ],
    founderMistakes: [
      "Buying discounted ingredient with no certificates, then being unable to use it because the specification could not be evidenced.",
      "Purchasing packaging from a closing brand without checking dimensions against their own line, and finding it unusable.",
      "Taking equipment as-is with no inspection, then paying more to make it usable than a new unit would have cost.",
      "Agreeing risk passed on collection and arranging transport casually, then absorbing damage in transit.",
      "Buying short-dated material that expired before the next production run.",
    ],
    negotiationPoints: [
      "Ask for an inspection period that runs from when you can actually assess the goods.",
      "Require the transfer of certificates, lot records, storage history and any specification documents with the goods.",
      "Get representations about storage conditions and provenance rather than relying on an as-is sale.",
      "Set title and risk to pass at a point that matches who is actually arranging transport.",
      "Agree a remedy for goods that do not match the description, even in a discounted sale.",
    ],
    redFlags: [
      "As-is with no inspection right at all.",
      "No documentation transferring with food-contact goods or ingredients.",
      "Full payment before inspection or collection.",
      "No description precise enough to say what was bought.",
      "Risk passing before you have any ability to inspect or protect the goods.",
    ],
    beforeYouSign: [
      "Physical inspection of the goods, or an agreed inspection right if that is not possible first.",
      "The certificates, lot records and storage history you will need to use the goods.",
      "Confirmation the goods actually fit your process, line or specification.",
    ],
    related: [
      "purchase-order-terms",
      "master-supply-agreement",
      "vendor-agreement",
      "ingredient-supply-agreement",
      "equipment-financing-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "master-supply-agreement",
    name: "Master Supply Agreement",
    category: "supply",
    subcategory: "Purchasing",
    aliases: ["MSA (Supply)", "Master Purchase Agreement", "Framework Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Supplier",
    whenYouNeedIt:
      "When you will buy repeatedly from the same supplier and want one set of terms sitting over every purchase order, instead of renegotiating or, more commonly, agreeing nothing at all.",
    whatItDoes:
      "Establishes the standing terms — pricing, quality, delivery, forecasting, liability, term — and lets individual purchase orders carry only the commercial details, while stating clearly which document wins when they conflict.",
    whyItMatters:
      "Without a master agreement, every purchase order is governed by whatever terms were attached to it, which usually means the supplier's. The practical effect is that a relationship you have had for two years has no agreed position on quality, on what happens when supply fails, or on who pays when a bad lot ruins a production run. A master agreement is also the document that makes forecasting safe: it is where you draw the line between planning numbers and a commitment to buy.",
    majorTerms: [
      "Scope: which materials and which sites the agreement covers",
      "Order of precedence between the master, purchase orders and any acknowledgment",
      "Pricing schedule, validity and escalation mechanism",
      "Forecasting obligations and the binding portion",
      "Minimum order quantities and lead times",
      "Delivery terms and acceptance",
      "Quality requirements and incorporation of any specification",
      "Change notification obligations",
      "Supply interruption, allocation and force majeure",
      "Warranties, indemnity and limitation of liability",
      "Insurance requirements",
      "Term, renewal, termination and last-time-buy rights",
    ],
    clausesToWatch: [
      {
        clause: "Order of precedence",
        why: "Without it, a supplier's acknowledgment form can override the master agreement you negotiated, one purchase order at a time.",
      },
      {
        clause: "Binding portion of the forecast",
        why: "A forecast that becomes a purchase obligation without a stated window turns planning into a commitment you did not know you made.",
      },
      {
        clause: "Force majeure and supply interruption",
        why: "A broad clause excusing the supplier from ordinary disruption leaves you with no supply and no remedy while their obligations pause.",
      },
      {
        clause: "Limitation of liability",
        why: "A cap set at the price of the material excludes the cost of the production run, the freight and the retailer consequences that a bad lot actually causes.",
      },
      {
        clause: "Automatic renewal and termination notice",
        why: "Long automatic renewals with early notice deadlines quietly extend a relationship you decided to leave.",
      },
      {
        clause: "Last-time-buy and transition rights",
        why: "Without them, a supplier's decision to discontinue a material gives you no time to qualify an alternative.",
      },
    ],
    cpgIssues: [
      "Incorporating the material specification and quality obligations into the master agreement is what stops each purchase order becoming a fresh argument about what conforming material means.",
      "Supply interruption in food and beverage is frequently seasonal or agricultural rather than catastrophic, so allocation language matters more than a generic force majeure clause.",
      "Certifications and regulatory documentation supporting label claims should be a standing obligation under the master agreement, with notice of any lapse, rather than a document chased per order.",
      "Recall cooperation and traceability response times belong in the master agreement so they apply to every lot, not only to the orders someone remembered to paper.",
      "A master agreement covering several materials should say which specification applies to which, or a single generic quality clause ends up governing very different inputs.",
    ],
    founderMistakes: [
      "Operating for years on purchase orders alone, so the supplier's standard terms governed a relationship nobody had negotiated.",
      "Signing a master agreement with no order-of-precedence clause, and having it displaced by acknowledgment forms.",
      "Providing a rolling forecast without a defined binding window, and receiving an invoice for material bought against it.",
      "Missing an automatic renewal notice deadline and being locked in for another full term.",
      "Accepting a liability cap at material value, then absorbing the cost of a ruined production run caused by a defective input.",
    ],
    negotiationPoints: [
      "Insist on an order-of-precedence clause making the master agreement control over purchase orders and acknowledgments.",
      "Define the binding forecast window narrowly and mark the rest as non-binding planning.",
      "Make force majeure mutual and exclude ordinary market and cost movement.",
      "Carve consequential production loss out of the liability cap, at least for defective material.",
      "Replace automatic multi-year renewal with a shorter term and a mutual notice period.",
      "Secure last-time-buy rights and a transition period if a material is discontinued.",
    ],
    redFlags: [
      "No order-of-precedence clause.",
      "Forecast converted into a binding commitment with no stated window.",
      "One-sided force majeure.",
      "Liability capped at the invoice value of the material.",
      "Automatic renewal with a long notice period to escape.",
      "No quality or specification incorporated by reference.",
    ],
    beforeYouSign: [
      "The specifications for every material the agreement will cover.",
      "A clear internal view of what your forecast actually commits you to.",
      "Your existing purchase order terms, checked for conflicts with the master.",
      "Certificates of insurance and current certification documents from the supplier.",
    ],
    related: [
      "purchase-order-terms",
      "ingredient-supply-agreement",
      "packaging-supply-agreement",
      "raw-material-supply-agreement",
      "supplier-quality-agreement",
      "vendor-agreement",
    ],
    attorneyReview: "yes",
    templateId: "master-supply-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "supplier-nda",
    name: "Supplier NDA",
    category: "supply",
    subcategory: "Purchasing",
    aliases: ["Vendor NDA", "Sourcing NDA", "Supplier Confidentiality Agreement"],
    stages: ["development", "pre-launch", "manufacturing", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Supplier",
    whenYouNeedIt:
      "Before sending a specification, a formula extract, a custom blend brief or your volume plans to a supplier for quoting — including suppliers who also serve your competitors, which is most of them.",
    whatItDoes:
      "Restricts what a supplier may do with what you send them: your specifications, custom blend composition, volumes, launch timing and the identity of the other suppliers in your chain.",
    whyItMatters:
      "Sourcing means describing your product to companies that sell to your entire category. A custom flavour brief, a functional blend ratio or a launch volume tells a supplier what you are building and when. Suppliers rarely misuse this deliberately, but without a use restriction there is nothing stopping the blend you developed being offered to the next brand that asks a similar question, and nothing stopping your volumes becoming a data point in someone else's sales conversation.",
    majorTerms: [
      "Definition of confidential information, including specifications and volumes",
      "No-use obligation in addition to no-disclosure",
      "Custom blend or formulation composition and who may use it",
      "Prohibition on supplying the same custom blend to third parties",
      "Duration of the obligation and treatment of trade secrets",
      "Return or destruction of documents and samples",
      "Exclusions for information already known or independently developed",
      "Whether obligations are mutual",
      "Extension to employees, affiliates and sub-suppliers",
    ],
    clausesToWatch: [
      {
        clause: "No-use versus no-disclosure",
        why: "A supplier who may not disclose your blend but may use it can offer the same composition to another customer without naming you.",
      },
      {
        clause: "Exclusivity over a custom blend",
        why: "Development work you paid for becomes a catalogue item unless the agreement says the blend is yours or exclusive to you.",
      },
      {
        clause: "Confidentiality of volumes and launch timing",
        why: "Your order volumes reveal your scale and your timing reveals your plans, both of which are valuable to competitors who share the supplier.",
      },
      {
        clause: "Duration of protection",
        why: "A short fixed term releases your specification on a date certain, regardless of whether it is still commercially sensitive.",
      },
      {
        clause: "Sub-supplier flow-down",
        why: "Suppliers pass briefs down to their own sources, and without a flow-down obligation the protection stops at the first tier.",
      },
    ],
    cpgIssues: [
      "A custom flavour or functional blend developed with a supplier is often the most distinctive part of a product, and the default position at many suppliers is that the composition is theirs.",
      "Suppliers serve entire categories, so the fact that you are launching a particular format at a particular volume is competitively useful information in itself.",
      "Physical samples sent for matching or evaluation can be analysed, so the agreement should cover samples as well as documents.",
      "Your supplier list is itself sensitive: a flavour house that knows your ingredient sources can reconstruct much of your cost structure.",
      "Suppliers frequently sub-contract or source through brokers, so the obligation needs to follow the information rather than stopping at the company you contracted with.",
    ],
    founderMistakes: [
      "Sending a custom blend brief to several suppliers with no agreement, and seeing a similar product appear in the category months later.",
      "Paying for blend development without securing exclusivity, and finding the blend offered as a stock item.",
      "Using a one-way NDA that protected the supplier's information but not the specification being sent to them.",
      "Never asking for return or destruction after a quote went nowhere.",
      "Sharing full annual volume forecasts during quoting with no confidentiality covering them.",
    ],
    negotiationPoints: [
      "Include an express no-use restriction covering the supplier's own products and other customers.",
      "Ask for exclusivity or ownership over any custom blend developed to your brief and at your cost.",
      "Cover volumes, forecasts and launch timing explicitly in the definition of confidential information.",
      "Extend obligations to sub-suppliers and brokers in the chain.",
      "Ask for indefinite protection for genuinely secret composition information.",
      "Require return or destruction of documents and samples on request.",
    ],
    redFlags: [
      "Disclosure-only obligations with no use restriction.",
      "Silence on ownership of a custom blend developed for you.",
      "A one-way agreement protecting only the supplier.",
      "No obligation covering physical samples.",
      "A short fixed confidentiality term with no trade secret carve-out.",
    ],
    beforeYouSign: [
      "A decision on what actually needs to be disclosed to get a useful quote.",
      "Clarity on who owns any custom development, agreed before the work starts.",
      "A list of the suppliers receiving the same information, so you know how widely it has travelled.",
    ],
    related: [
      "manufacturing-nda",
      "mutual-nda",
      "ingredient-supply-agreement",
      "flavor-house-agreement",
      "trade-secret-protection-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "vendor-agreement",
    name: "Vendor Agreement",
    category: "supply",
    subcategory: "Purchasing",
    aliases: ["Supplier Services Agreement", "General Vendor Terms", "Goods and Services Agreement"],
    stages: ["pre-launch", "launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand (buyer) + Vendor",
    whenYouNeedIt:
      "For the recurring suppliers that do not fit a specialised agreement: sanitation services, laboratory testing, pallets, dry goods, equipment maintenance, small-scale outsourced work around the plant or warehouse.",
    whatItDoes:
      "Sets general commercial terms for buying goods or services from a vendor: scope, pricing, invoicing, insurance, indemnity, subcontracting, confidentiality, compliance documentation and termination.",
    whyItMatters:
      "The word 'vendor agreement' points in two directions and founders mix them up. Here you are the buyer setting terms for a supplier. In a retail context the same phrase means the document where you are the vendor and the retailer sets the terms. Getting the direction right matters because the protective clauses sit on opposite sides. This is also the agreement that catches the long tail of suppliers who touch your product or your facility without ever being treated as part of the supply chain.",
    majorTerms: [
      "Scope of goods or services supplied",
      "Pricing, rate card and how changes are handled",
      "Invoicing and payment terms",
      "Insurance requirements and additional insured status",
      "Indemnity and limitation of liability",
      "Subcontracting and approval rights",
      "Access to your facility or your third-party warehouse",
      "Confidentiality of anything they see on site",
      "Compliance documentation required from the vendor",
      "Term, termination and notice",
    ],
    clausesToWatch: [
      {
        clause: "Insurance and additional insured status",
        why: "A vendor working in a facility where your product is made or stored can cause a loss far larger than the value of their contract.",
      },
      {
        clause: "Subcontracting rights",
        why: "Unrestricted subcontracting means people you never approved get access to your product, your facility or your data.",
      },
      {
        clause: "Limitation of liability",
        why: "A cap at the fees paid is meaningless when a testing error or a sanitation failure affects an entire production run.",
      },
      {
        clause: "Automatic renewal and price escalation",
        why: "Small recurring vendor contracts renew quietly with annual increases nobody reviews, and they accumulate.",
      },
      {
        clause: "Confidentiality covering site observations",
        why: "Vendors working on site see your volumes, your customers on the dock and your process, none of which is protected unless the agreement says so.",
      },
    ],
    cpgIssues: [
      "Laboratory vendors produce the results you rely on to release product, so their standard of care and their liability position matter more than their price.",
      "Sanitation and pest control vendors operate inside a food facility, and a failure there becomes a food safety event attributed to your product.",
      "Any vendor working at your co-packer or 3PL is inside someone else's building, so the insurance and access terms need to work with that facility's requirements too.",
      "Vendors on site see finished goods, retailer labels on pallets and production volumes, which is competitively useful information in a small category.",
      "Testing and certification vendors should provide documentation in a form a retailer will accept, not just a result you can read.",
    ],
    founderMistakes: [
      "Engaging a testing laboratory on their standard terms with liability capped at the test fee, then absorbing the cost of a run released on a wrong result.",
      "Letting a vendor subcontract work inside a co-packer's facility without approval, and breaching the co-packer's own access rules.",
      "Signing recurring vendor agreements with automatic annual increases and never reviewing them.",
      "Not requiring certificates of insurance from vendors working around inventory, and having no recourse after a loss.",
      "Confusing this agreement with a retailer's vendor agreement and negotiating the wrong side of every clause.",
    ],
    negotiationPoints: [
      "Require certificates of insurance appropriate to the work and to the facility they will enter.",
      "Restrict subcontracting to approved parties, with the same obligations flowing down.",
      "Push back on liability caps set at fees paid where the vendor's error can affect product.",
      "Replace automatic renewal with a fixed term and a mutual notice period.",
      "Add confidentiality covering anything observed at your facility or a third-party site.",
    ],
    redFlags: [
      "No insurance requirement for a vendor entering a production or storage facility.",
      "Unrestricted subcontracting.",
      "Liability capped at the fees paid where product is at risk.",
      "Automatic renewal with built-in annual increases.",
      "No confidentiality obligation at all.",
    ],
    beforeYouSign: [
      "Certificates of insurance and confirmation the coverage meets your co-packer's or warehouse's access requirements.",
      "A clear scope, so the recurring charge matches recurring work.",
      "Confirmation of whether this vendor's output is relied on for a product release decision.",
    ],
    related: [
      "master-supply-agreement",
      "purchase-order-terms",
      "purchase-agreement",
      "retail-vendor-agreement",
      "supplier-nda",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "purchase-order-terms",
    name: "Purchase Order Terms & Conditions",
    category: "supply",
    subcategory: "Purchasing",
    aliases: ["PO Terms", "Standard Terms of Purchase", "Buyer Terms and Conditions"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand (buyer) + Supplier",
    whenYouNeedIt:
      "As soon as you issue purchase orders regularly, and before any significant buying happens without a master agreement behind it.",
    whatItDoes:
      "Sets the standing terms attached to every purchase order you issue: what acceptance means, delivery and packing requirements, inspection and rejection rights, cancellation, invoicing, and the documentation that must accompany a shipment.",
    whyItMatters:
      "Most CPG buying happens on purchase orders, and most purchase orders travel with whatever terms the supplier attaches to their acknowledgment. When your terms and theirs conflict, which set governs is a genuinely contested question that turns on the documents and the sequence — worth asking counsel about once, and worth having your own terms in place either way. Without them, the default position is that you bought on their terms: short rejection windows, no documentation requirement, and no consequence for a late delivery that costs you a production slot.",
    majorTerms: [
      "How an order is accepted and when it becomes binding",
      "Delivery date treated as a firm requirement",
      "Packing, labelling, palletisation and routing requirements",
      "Documentation required with shipment, including certificates of analysis",
      "Inspection period and rejection rights",
      "Remedies for late, short or non-conforming delivery",
      "Cancellation and change order rights",
      "Price firmness and no unilateral increases after acceptance",
      "Invoicing requirements and payment terms",
      "Warranties required from the supplier",
      "Insurance and indemnity",
      "Conflict with the supplier's own terms",
    ],
    clausesToWatch: [
      {
        clause: "Which terms govern on conflict",
        why: "Your purchase order and their acknowledgment often disagree, and the outcome affects rejection rights, warranties and liability on every order you place.",
      },
      {
        clause: "Inspection and rejection window",
        why: "A window that starts at delivery expires long before ingredient is opened at the co-packer, which is where a defect is actually found.",
      },
      {
        clause: "Delivery date obligations",
        why: "If the delivery date is aspirational, a late shipment costs you a booked production slot with no consequence for the supplier.",
      },
      {
        clause: "Required shipping documentation",
        why: "Material arriving without a COA or lot documentation may be unusable, and the requirement has to be on the order rather than assumed.",
      },
      {
        clause: "Price firmness after acceptance",
        why: "Without it, a supplier can reprice between order and delivery on a component whose lead time is measured in months.",
      },
      {
        clause: "Cancellation and change rights",
        why: "One-sided cancellation terms let a supplier drop an order while you pay a fee to change one.",
      },
    ],
    cpgIssues: [
      "Every ingredient shipment should arrive with a certificate of analysis referencing the lot, and the purchase order is the practical place to make that a condition of delivery.",
      "Packing and pallet requirements matter when material ships directly to a co-packer or a 3PL, both of which have receiving rules of their own.",
      "Temperature and handling requirements for chilled or sensitive materials belong on the order, because carriers follow the paperwork they are given.",
      "Inspection of ingredients often happens when the material is opened at the plant, weeks after delivery, so the rejection window needs to reflect that reality.",
      "Lot coding and date marking requirements on incoming material determine whether you can trace it later, and suppliers vary widely unless told.",
      "Short-dated deliveries are a recurring problem; a minimum remaining shelf life on receipt is a term worth putting on every order.",
    ],
    founderMistakes: [
      "Issuing purchase orders with no terms at all, so the supplier's acknowledgment terms governed every purchase by default.",
      "Never requiring a COA with delivery, then having no documentation when a retailer asked about an ingredient.",
      "Accepting deliveries with only weeks of shelf life remaining because no minimum was specified on the order.",
      "Having no consequence for late delivery, and losing a booked production slot with no recourse.",
      "Letting a supplier reprice between order and delivery on a long-lead component.",
    ],
    negotiationPoints: [
      "Have your own purchase order terms drafted once and attached to every order.",
      "Set the inspection window to run from when material is used or first opened at the plant.",
      "Make delivery dates firm, with a stated remedy for late or short delivery.",
      "Require a COA and lot documentation as a condition of acceptance.",
      "Specify a minimum remaining shelf life on receipt for every dated material.",
      "State that the order price is firm and cannot be changed after acceptance.",
    ],
    redFlags: [
      "Purchase orders issued with no terms attached.",
      "Rejection windows measured in a few days from delivery.",
      "No documentation requirement for ingredient shipments.",
      "Delivery dates described as estimates only.",
      "Supplier free to reprice after accepting the order.",
    ],
    beforeYouSign: [
      "Your own standard purchase order terms, reviewed once and used consistently.",
      "The receiving requirements of your co-packer and 3PL, so packing and routing terms match them.",
      "A view of which suppliers routinely return their own conflicting terms, so those relationships get a master agreement instead.",
    ],
    related: [
      "master-supply-agreement",
      "ingredient-supply-agreement",
      "packaging-supply-agreement",
      "vendor-agreement",
      "purchase-agreement",
    ],
    attorneyReview: "depends",
    templateId: "purchase-order-terms",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
];

export default SUPPLY_DOCS;
